import { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { setUserContext, trackEvent, trackPageview } from "../core/analytics/ga4";
import { activeNavId } from "../components/layout/navigation";
import { UserContext } from "../contexts/User.jsx";
import { useEntitlements } from "../contexts/Entitlements.jsx";

// Fires a GA4 page_view on every route change (including initial mount), tagged
// with the signed-in user, their role and the store they are working in. Must
// render inside <BrowserRouter>, <UserContextProvider> and <EntitlementsProvider>.

/** The user's role IN the active store — store_user seat, not a global flag. */
function roleIn(user, store) {
  if (!user) return null;
  if (user.is_master_admin) return "master_admin";
  return store?.role || (user.is_admin ? "admin" : "client");
}

export default function GA4PageTracker() {
  const { pathname, search } = useLocation();
  const { user, activeStoreId, accessibleStores } = useContext(UserContext) || {};
  const { plan } = useEntitlements() || {};

  const id = user?.id ?? null;
  const isTeam = !!user?.is_master_admin;
  // The ACTIVE store: what the store switcher selected, else the home store.
  // A master admin with nothing selected is on the unscoped Master store.
  const storeId = activeStoreId ?? (isTeam ? null : user?.store_id_m ?? null);
  const store = (accessibleStores || []).find((s) => s.id === storeId);
  const storeName = store?.name ?? null;
  const role = roleIn(user, store);

  const lastStore = useRef(undefined);

  // Declared before the page_view effect so the first hit already carries the user.
  useEffect(() => {
    setUserContext(id ? { id, isTeam, storeId, storeName, role, plan } : null);
    if (!id) return;

    try {
      if (!sessionStorage.getItem("ga4_login_sent")) {
        sessionStorage.setItem("ga4_login_sent", "1");
        trackEvent("login", { method: "session" });
      }
    } catch {
      // storage blocked — skip the login event, keep tracking
    }
    if (lastStore.current !== undefined && lastStore.current !== storeId) {
      trackEvent("store_switch", { from_store_id: lastStore.current ? String(lastStore.current) : "master" });
    }
    lastStore.current = storeId;
  }, [id, isTeam, storeId, storeName, role, plan]);

  useEffect(() => {
    trackPageview(pathname + search, { feature: activeNavId(pathname) });
  }, [pathname, search]);

  return null;
}
